import React, { useState } from "react";
import { X, Ruler } from "lucide-react";

/**
 * Asvasa size guide modal.
 * Reads `category` from the product and shows the appropriate chart.
 * Toggles between inches and cm.
 */
const CHARTS = {
  shirts: {
    title: "Shirts · Size Guide",
    note: "Brand size maps to your chest measurement in inches.",
    columns: ["Size", "Chest", "Front Length", "Across Shoulder", "Sleeve Length"],
    rowsIn: [
      { Size: "S (38)", Chest: 38, "Front Length": 26.5, "Across Shoulder": 17.5, "Sleeve Length": 25 },
      { Size: "M (40)", Chest: 40, "Front Length": 27.0, "Across Shoulder": 18.0, "Sleeve Length": 25.5 },
      { Size: "L (42)", Chest: 42, "Front Length": 27.5, "Across Shoulder": 18.75, "Sleeve Length": 26 },
      { Size: "XL (44)", Chest: 44, "Front Length": 28.0, "Across Shoulder": 19.5, "Sleeve Length": 26.5 },
      { Size: "XXL (46)", Chest: 46, "Front Length": 28.5, "Across Shoulder": 20.25, "Sleeve Length": 27 },
    ],
  },
  kurtas: {
    title: "Short Kurtas · Size Guide",
    note: "Brand size maps to your chest measurement in inches.",
    columns: ["Size", "Chest", "Length", "Shoulder", "Sleeve Length"],
    rowsIn: [
      { Size: "S (39)", Chest: 41, Length: 28, Shoulder: 17.7, "Sleeve Length": 24.8 },
      { Size: "M (40)", Chest: 42, Length: 28.5, Shoulder: 18.0, "Sleeve Length": 25.1 },
      { Size: "L (42)", Chest: 44, Length: 29, Shoulder: 18.6, "Sleeve Length": 25.5 },
      { Size: "XL (44)", Chest: 48, Length: 30, Shoulder: 20.0, "Sleeve Length": 25.8 },
      { Size: "XXL (46)", Chest: 50, Length: 31, Shoulder: 20.6, "Sleeve Length": 26.0 },
    ],
  },
  "long-kurta": {
    title: "Long Kurtas · Size Guide",
    note: "Brand size maps to your chest measurement in inches. Length runs longer for the elevated silhouette.",
    columns: ["Size", "Chest", "Length", "Shoulder", "Sleeve Length"],
    rowsIn: [
      { Size: "S (39)", Chest: 41, Length: 40, Shoulder: 17.7, "Sleeve Length": 24.8 },
      { Size: "M (40)", Chest: 42, Length: 41, Shoulder: 18.0, "Sleeve Length": 25.1 },
      { Size: "L (42)", Chest: 44, Length: 42, Shoulder: 18.6, "Sleeve Length": 25.5 },
      { Size: "XL (44)", Chest: 48, Length: 44, Shoulder: 20.0, "Sleeve Length": 25.8 },
      { Size: "XXL (46)", Chest: 50, Length: 46, Shoulder: 20.6, "Sleeve Length": 26.0 },
    ],
  },
  "long-kurtas": {
    title: "Long Kurtas · Size Guide",
    note: "Brand size maps to your chest measurement in inches. Length runs longer for the elevated silhouette.",
    columns: ["Size", "Chest", "Length", "Shoulder", "Sleeve Length"],
    rowsIn: [
      { Size: "S (39)", Chest: 41, Length: 40, Shoulder: 17.7, "Sleeve Length": 24.8 },
      { Size: "M (40)", Chest: 42, Length: 41, Shoulder: 18.0, "Sleeve Length": 25.1 },
      { Size: "L (42)", Chest: 44, Length: 42, Shoulder: 18.6, "Sleeve Length": 25.5 },
      { Size: "XL (44)", Chest: 48, Length: 44, Shoulder: 20.0, "Sleeve Length": 25.8 },
      { Size: "XXL (46)", Chest: 50, Length: 46, Shoulder: 20.6, "Sleeve Length": 26.0 },
    ],
  },
};

const inToCm = (v) => Math.round(v * 2.54 * 10) / 10;

export default function SizeGuideModal({ category = "shirts", open, onClose }) {
  const [unit, setUnit] = useState("in");
  if (!open) return null;
  const data = CHARTS[category] || CHARTS.shirts;
  const rows = data.rowsIn.map((r) => {
    if (unit === "in") return r;
    const out = { ...r };
    data.columns.slice(1).forEach((c) => {
      out[c] = inToCm(r[c]);
    });
    return out;
  });

  return (
    <div
      className="fixed inset-0 z-[70] bg-[#1A1A1A]/70 backdrop-blur-sm flex items-center justify-center px-4 py-10"
      onClick={onClose}
      data-testid="size-guide-modal"
    >
      <div
        className="bg-[#c3ba9e] max-w-3xl w-full max-h-[88vh] overflow-y-auto border border-[#1A1A1A]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 md:px-8 py-5 border-b border-[#a89e80]">
          <div className="flex items-center gap-3">
            <Ruler size={18} className="text-[#1A1A1A]" />
            <div>
              <p className="eyebrow">Asvasa</p>
              <h2 className="font-serif-display text-2xl md:text-3xl mt-1">{data.title}</h2>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close size guide" className="text-[#1A1A1A] hover:text-[#4A4A4A]" data-testid="size-guide-close">
            <X size={20} />
          </button>
        </div>

        {/* Unit toggle */}
        <div className="flex items-center justify-between px-6 md:px-8 pt-5">
          <p className="text-[12px] text-[#4A4A4A] max-w-md">{data.note}</p>
          <div className="inline-flex border border-[#1A1A1A]">
            {["in", "cm"].map((u) => (
              <button
                key={u}
                onClick={() => setUnit(u)}
                className={`px-4 py-1.5 text-[11px] tracking-[0.22em] uppercase transition-colors ${
                  unit === u ? "bg-[#1A1A1A] text-[#c3ba9e]" : "text-[#1A1A1A] hover:bg-[#b8af92]"
                }`}
                data-testid={`size-guide-unit-${u}`}
              >
                {u}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="px-6 md:px-8 py-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1A1A1A]">
                {data.columns.map((c) => (
                  <th key={c} className="text-left py-3 text-[10px] tracking-[0.28em] uppercase text-[#4A4A4A] font-medium">
                    {c} {c !== "Size" && <span className="text-[#7A7A7A]">({unit})</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-[#a89e80]/60">
                  {data.columns.map((c, j) => (
                    <td key={c} className={`py-3 ${j === 0 ? "font-body text-[15px]" : "text-[#4A4A4A]"}`}>
                      {r[c]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* How to measure */}
        <div className="px-6 md:px-8 pb-8 grid sm:grid-cols-2 gap-6 text-[13px] text-[#4A4A4A]">
          <div>
            <p className="eyebrow mb-2">Chest</p>
            <p>Measure around the fullest part of your chest, keeping the tape level under the armpits.</p>
          </div>
          <div>
            <p className="eyebrow mb-2">Shoulder</p>
            <p>Measure across the back from one shoulder seam to the other.</p>
          </div>
          <div>
            <p className="eyebrow mb-2">Length</p>
            <p>From the highest point of the shoulder seam down to the hem.</p>
          </div>
          <div>
            <p className="eyebrow mb-2">Sleeve</p>
            <p>From the shoulder seam, along the outside of the arm to the wrist.</p>
          </div>
        </div>
        <p className="px-6 md:px-8 pb-6 text-[11px] text-[#7A7A7A] italic">
          Sizes may vary by 2–3 cm as each piece is hand-finished. 1 inch = 2.54 cm.
        </p>
      </div>
    </div>
  );
}
